import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, Button, IconButton, Drawer, List, 
  ListItem, ListItemIcon, ListItemText, Box, Avatar, Menu, MenuItem,
  Divider, useMediaQuery, useTheme, Switch, FormControlLabel
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import BarChartIcon from '@mui/icons-material/BarChart';
import AuthContext from '../contexts/AuthContext';
import NotificationIcon from './NotificationIcon';

// Largeur du drawer
const drawerWidth = 240;

const Navbar = () => {
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPinned, setDrawerPinned] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const isAccueilOnlyMenu = isAuthenticated && user?.role === 'ACCEUIL';

  // Récupérer l'état du drawer depuis le localStorage
  useEffect(() => {
    const pinnedState = localStorage.getItem('drawerPinned') === 'true';
    setDrawerPinned(pinnedState);
    if (pinnedState && !isMobile) {
      setDrawerOpen(true);
    }
  }, [isMobile]);

  // Fermer le drawer lors du changement de route sur mobile
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [location, isMobile]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    if (!drawerPinned) {
      setDrawerOpen(false);
    }
  };

  const handleDrawerPin = () => {
    const newPinnedState = !drawerPinned;
    setDrawerPinned(newPinnedState);
    localStorage.setItem('drawerPinned', newPinnedState.toString());
    
    // Si on désépingle et qu'on est sur desktop, on ferme le drawer
    if (!newPinnedState && !isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = (e) => {
    e.preventDefault();
    handleMenuClose();
    logout();
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: '#006d78',
          width: { sm: drawerOpen && drawerPinned ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { sm: drawerOpen && drawerPinned ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          {(!drawerOpen || !drawerPinned || isMobile) && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerOpen}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" component={Link} to="/" sx={{ flexGrow: 1, textDecoration: 'none', color: 'inherit' }}>
            Gestion Electronique des Visites et Absences Administratives
          </Typography>
          {isAuthenticated ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button color="inherit" component={Link} to="/profile">
                {user?.firstName} {user?.lastName}
              </Button>
              <NotificationIcon />
              <IconButton color="inherit" onClick={handleMenuOpen}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem component={Link} to="/profile" onClick={handleMenuClose}>
                  Profil
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  Déconnexion
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <>
              <Button color="inherit" component={Link} to="/appointment" sx={{ mr: 1 }}>
                Prendre un rendez-vous
              </Button>
              <Button color="inherit" component={Link} to="/login">
                Connexion
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant={drawerPinned && !isMobile ? "permanent" : "temporary"}
        open={drawerOpen}
        onClose={handleDrawerClose}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: `2px solid ${theme.palette.divider}`,
            borderLeft: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backgroundColor: '#006d78',
            color: '#ffffff',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '8px 16px',
          borderBottom: `2px solid #005a63`,
          backgroundColor: '#005a63',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>Menu</Typography>
          <Box>
            {!isMobile && (
              <IconButton 
                onClick={handleDrawerPin}
                sx={{
                  border: '1px solid',
                  borderColor: theme.palette.primary.contrastText,
                  color: theme.palette.primary.contrastText,
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                {drawerPinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
              </IconButton>
            )}
            <IconButton 
              onClick={handleDrawerClose}
              sx={{
                border: '1px solid',
                borderColor: theme.palette.primary.contrastText,
                color: theme.palette.primary.contrastText,
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                ml: 1,
                '&:hover': {
                  transform: 'scale(1.1)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
        </Box>
        <Divider sx={{ borderWidth: '2px', borderColor: theme.palette.primary.dark }} />
        <List sx={{ 
          py: 1, 
          '& .MuiListItemText-primary': { color: theme.palette.primary.contrastText },
          '& .MuiListItemIcon-root': { color: theme.palette.primary.contrastText }
        }}>
          {!isAccueilOnlyMenu && (
            <ListItem 
              button 
              component={Link} 
              to="/" 
              onClick={isMobile ? handleDrawerClose : undefined}
              selected={location.pathname === '/'}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                color: theme.palette.primary.contrastText,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Accueil" primaryTypographyProps={{ color: theme.palette.primary.contrastText }} />
            </ListItem>
          )}
          
          {!isAuthenticated && (
            <ListItem 
              button 
              component={Link} 
              to="/appointment" 
              onClick={isMobile ? handleDrawerClose : undefined}
              selected={location.pathname === '/appointment'}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                color: theme.palette.primary.contrastText,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><EventIcon /></ListItemIcon>
              <ListItemText primary="Prendre un rendez-vous" />
            </ListItem>
          )}
          
          {isAuthenticated && (
            <>
              {!isAccueilOnlyMenu && user?.role !== 'VISITOR' && (
              <ListItem 
                button 
                component={Link} 
                to="/dashboard" 
                onClick={isMobile ? handleDrawerClose : undefined}
                selected={location.pathname === '/dashboard'}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><DashboardIcon /></ListItemIcon>
                <ListItemText primary="Tableau de bord visites" />
              </ListItem>
              )}

              {(user?.role === 'ADMIN' || user?.role === 'RH' || user?.role === 'HOST' || user?.role === 'MANAGER') && (
                <ListItem 
                  button 
                  component={Link} 
                  to="/absence-dashboard" 
                  onClick={isMobile ? handleDrawerClose : undefined}
                  selected={location.pathname === '/absence-dashboard'}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary="Tableau de bord absences" />
                </ListItem>
              )}
              
              {!isAccueilOnlyMenu && (
              <ListItem 
                button 
                component={Link} 
                to="/profile" 
                onClick={isMobile ? handleDrawerClose : undefined}
                selected={location.pathname === '/profile'}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><AccountCircleIcon /></ListItemIcon>
                <ListItemText primary="Profil" />
              </ListItem>
              )}
              
              {user?.role !== 'VISITOR' && (
                <ListItem 
                  button 
                  component={Link} 
                  to="/visits" 
                  onClick={isMobile ? handleDrawerClose : undefined}
                  selected={location.pathname === '/visits'}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderLeft: '3px solid',
                    borderLeftColor: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    },
                  },
                  color: theme.palette.primary.contrastText,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><EventIcon /></ListItemIcon>
                  <ListItemText primary="Visites" />
                </ListItem>
              )}
              
              {(user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'ACCEUIL' || user?.role === 'HOST' || user?.role === 'VISITOR') && (
                <ListItem 
                  button 
                  component={Link} 
                  to="/appointments" 
                  onClick={isMobile ? handleDrawerClose : undefined}
                  selected={location.pathname === '/appointments'}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><EventIcon /></ListItemIcon>
                  <ListItemText primary="Rendez-vous" />
                </ListItem>
              )}
              
              {/* Liens pour la gestion des absences */}
              {(user?.role !== 'VISITOR' && user?.role !== 'ACCEUIL') && (
                <ListItem 
                  button 
                  component={Link} 
                  to="/absences" 
                  onClick={isMobile ? handleDrawerClose : undefined}
                  selected={location.pathname === '/absences'}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}>
                    <CalendarTodayIcon />
                  </ListItemIcon>
                  <ListItemText primary="Absences" />
                </ListItem>
               )}
              
              {/* {['MANAGER'].includes(user?.role) && (
                <>
                  <ListItem 
                    button 
                    component={Link} 
                    to="/users" 
                    onClick={isMobile ? handleDrawerClose : undefined}
                    selected={location.pathname === '/users'}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><PeopleIcon /></ListItemIcon>
                    <ListItemText primary="Utilisateurs" />
                  </ListItem>
                  <ListItem 
                    button 
                    component={Link} 
                    to="/functions" 
                    onClick={isMobile ? handleDrawerClose : undefined}
                    selected={location.pathname === '/functions'}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><BusinessIcon /></ListItemIcon>
                    <ListItemText primary="Fonctions" />
                  </ListItem>
                </>
              )} */}
              
              { ['MANAGER', 'ADMIN'].includes(user?.role) && (
                <>
                  <ListItem 
                    button 
                    component={Link} 
                    to="/users" 
                    onClick={isMobile ? handleDrawerClose : undefined}
                    selected={location.pathname === '/users'}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><PeopleIcon /></ListItemIcon>
                    <ListItemText primary="Utilisateurs" />
                  </ListItem>
                  <ListItem 
                    button 
                    component={Link} 
                    to="/visitors" 
                    onClick={isMobile ? handleDrawerClose : undefined}
                    selected={location.pathname === '/visitors'}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><PeopleIcon /></ListItemIcon>
                    <ListItemText primary="Visiteurs" />
                  </ListItem>
                  <ListItem 
                    button 
                    component={Link} 
                    to="/departments" 
                    onClick={isMobile ? handleDrawerClose : undefined}
                    selected={location.pathname === '/departments'}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><BusinessIcon /></ListItemIcon>
                    <ListItemText primary="Départements" />
                  </ListItem>
                  <ListItem 
                    button 
                    component={Link} 
                    to="/functions" 
                    onClick={isMobile ? handleDrawerClose : undefined}
                    selected={location.pathname === '/functions'}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><BusinessIcon /></ListItemIcon>
                    <ListItemText primary="Fonctions" />
                  </ListItem>
                </>
              )}
              
              {(user?.role === 'ADMIN' || user?.role === 'RH') && (
                <ListItem 
                  button 
                  component={Link} 
                  to="/absence-types" 
                  onClick={isMobile ? handleDrawerClose : undefined}
                  selected={location.pathname === '/absence-types'}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderLeft: '3px solid',
                  borderLeftColor: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
                color: theme.palette.primary.contrastText,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}>
                    <CategoryIcon />
                  </ListItemIcon>
                  <ListItemText primary="Types d'absence" />
                </ListItem>
              )}
              
              <Divider sx={{ my: 1, borderWidth: '2px', borderColor: '#005a63' }} />
              
              <ListItem 
                button 
                onClick={(e) => { 
                  e.preventDefault();
                  if (isMobile) handleDrawerClose(); 
                  logout(); 
                }}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 2px 8px rgba(244, 67, 54, 0.2)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.contrastText }}><ExitToAppIcon /></ListItemIcon>
                <ListItemText primary="Déconnexion" />
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
      
      {/* Ajouter un espace pour compenser la barre d'application fixe */}
      <Toolbar sx={{ minHeight: '48px !important' }} />
    </>
  );
};

export default Navbar; 
